import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, Users, Shield, ShieldCheck, User } from '@phosphor-icons/react';

const roles = [
  { value: 'admin', label: 'Yönetici', icon: ShieldCheck, description: 'Tüm yetkilere sahip' },
  { value: 'manager', label: 'Müdür', icon: Shield, description: 'Tedarikçi ve sayım yetkileri' },
  { value: 'staff', label: 'Personel', icon: User, description: 'Temel işlemler' },
];

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Bu sayfayı görüntüleme yetkiniz yok');
      } else {
        toast.error('Kullanıcılar yüklenirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
      });
    } else {
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', role: 'staff' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Ad ve e-posta gerekli');
      return;
    }
    
    if (!editingUser && !formData.password) {
      toast.error('Yeni kullanıcı için şifre gerekli');
      return;
    }
    
    try {
      if (editingUser) {
        await usersAPI.update(editingUser.id, {
          name: formData.name,
          role: formData.role,
        });
        toast.success('Kullanıcı güncellendi');
      } else {
        await usersAPI.create(formData);
        toast.success('Kullanıcı oluşturuldu');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await usersAPI.update(userId, { is_active: !currentStatus });
      toast.success(currentStatus ? 'Kullanıcı devre dışı bırakıldı' : 'Kullanıcı aktifleştirildi');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) return;
    
    try {
      await usersAPI.delete(userId);
      toast.success('Kullanıcı silindi');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  const getRoleBadge = (role) => {
    const r = roles.find(ro => ro.value === role) || roles[2];
    const colorClasses = {
      admin: 'bg-primary/10 text-primary',
      manager: 'bg-secondary/10 text-secondary',
      staff: 'bg-muted text-muted-foreground',
    };
    return (
      <span className={`badge-pill ${colorClasses[role] || colorClasses.staff}`}>
        {r.label}
      </span>
    );
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <Shield size={48} weight="duotone" className="text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">Bu sayfayı görüntüleme yetkiniz yok</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
              Kullanıcı Yönetimi
            </h1>
            <p className="mt-2 text-muted-foreground">
              Sistem kullanıcılarını ve yetkilerini yönetin
            </p>
          </div>
          <Button onClick={() => handleOpenDialog()} className="bg-primary hover:bg-primary/90" data-testid="add-user-btn">
            <Plus size={20} weight="bold" className="mr-2" />
            Yeni Kullanıcı
          </Button>
        </div>

        {/* Role explanation cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {roles.map((role) => {
            const Icon = role.icon;
            return (
              <div key={role.value} className="border border-border bg-card p-4 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted">
                    <Icon size={20} weight="duotone" className="text-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">{role.label}</p>
                    <p className="text-xs text-muted-foreground">{role.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Users Table */}
        <div className="border border-border bg-card rounded-xl overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow className="border-b border-border bg-muted/30">
                <TableHead className="py-4">Kullanıcı</TableHead>
                <TableHead>E-posta</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Kayıt Tarihi</TableHead>
                <TableHead className="text-center">Aktif</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Users size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">Henüz kullanıcı bulunmuyor</p>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} className="table-row-hover border-b border-border/40" data-testid={`user-row-${user.id}`}>
                    <TableCell className="py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium">{user.name}</span>
                        {user.id === currentUser?.id && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Sen</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(user.created_at)}</TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={user.is_active}
                        onCheckedChange={() => handleToggleActive(user.id, user.is_active)}
                        disabled={user.id === currentUser?.id}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(user)} disabled={user.id === currentUser?.id}>
                          <PencilSimple size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(user.id)}
                          disabled={user.id === currentUser?.id}
                        >
                          <Trash size={18} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Ad Soyad *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ahmet Yılmaz"
                  data-testid="user-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>E-posta *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ahmet@restoran.com"
                  disabled={editingUser}
                  data-testid="user-email-input"
                />
              </div>
              
              {!editingUser && (
                <div className="space-y-2">
                  <Label>Şifre *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="user-password-input"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label} - {role.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="user-submit">
                  {editingUser ? 'Güncelle' : 'Oluştur'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
